/**
 * Compress and resize images before upload to reduce file size.
 * Uses canvas API — runs entirely in the browser.
 * SERIAL compression: processes one image at a time to avoid freezing.
 */

export interface CompressionOptions {
  maxWidth?: number;
  maxHeight?: number;
  quality?: number;
  mimeType?: string;
}

const DEFAULT_OPTIONS: CompressionOptions = {
  maxWidth: 2048,
  maxHeight: 2048,
  quality: 0.85,
  mimeType: "image/jpeg",
};

/**
 * Compresses a single image file by resizing and re-encoding it.
 * Returns a new File with reduced size.
 */
export async function compressImage(
  file: File,
  options: CompressionOptions = {}
): Promise<File> {
  const opts = { ...DEFAULT_OPTIONS, ...options };

  // Skip non-image files
  if (!file.type.startsWith("image/")) {
    return file;
  }

  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(url);

      let { width, height } = img;

      // Calculate new dimensions maintaining aspect ratio
      if (width > opts.maxWidth! || height > opts.maxHeight!) {
        const ratio = Math.min(
          opts.maxWidth! / width,
          opts.maxHeight! / height
        );
        width = Math.round(width * ratio);
        height = Math.round(height * ratio);
      }

      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext("2d");
      if (!ctx) {
        reject(new Error("Canvas context not available"));
        return;
      }

      // Use better quality rendering
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = "high";
      ctx.drawImage(img, 0, 0, width, height);

      const mimeType =
        file.type === "image/png" && opts.quality === 1
          ? "image/png"
          : opts.mimeType || "image/jpeg";

      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error("Canvas blob conversion failed"));
            return;
          }

          const compressedFile = new File([blob], file.name, {
            type: mimeType,
            lastModified: file.lastModified,
          });

          console.log(
            `[compress] ${file.name}: ${(file.size / 1024 / 1024).toFixed(2)}MB -> ${(compressedFile.size / 1024 / 1024).toFixed(2)}MB`
          );

          resolve(compressedFile);
        },
        mimeType,
        opts.quality
      );
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      // If compression fails, return original file
      resolve(file);
    };

    img.src = url;
  });
}

/**
 * Compress multiple images ONE AT A TIME (serial).
 * This prevents the browser from freezing when processing many large files.
 */
export async function compressImagesSerial(
  files: File[],
  options?: CompressionOptions,
  onProgress?: (current: number, total: number, fileName: string) => void
): Promise<File[]> {
  const results: File[] = [];

  for (let i = 0; i < files.length; i++) {
    onProgress?.(i + 1, files.length, files[i].name);
    const compressed = await compressImage(files[i], options);
    results.push(compressed);
    // Small yield to keep UI responsive
    await new Promise((resolve) => setTimeout(resolve, 10));
  }

  return results;
}

/**
 * @deprecated Use compressImagesSerial instead to avoid browser freezing.
 * Compress multiple images in parallel (fast but can freeze on many large files).
 */
export async function compressImages(
  files: File[],
  options?: CompressionOptions
): Promise<File[]> {
  return Promise.all(files.map((f) => compressImage(f, options)));
}